/**
 * BatNestEntity — a destructible bat nest placed on maze bridge-wall cells.
 *
 * The nest sits on a bridge-wall cell (even-indexed cell between two path
 * cells). During placement, BatNestSystem converts the wall cell to PATH and
 * removes the original wall sprite so the nest replaces the wall visually.
 * Adjacent wall cells receive crumbled-wall overlays to convey partial
 * destruction.
 *
 * Walking onto a nest cell is possible but costly — the player suffers a
 * traversal penalty (2× damage + 2× coin loss).
 *
 * When the player approaches within the activation radius, the nest releases
 * a swarm of bats. After a cooldown period, it can activate again on
 * subsequent approaches.
 *
 * The nest can be destroyed with the Hammer of Threia — 3 successful B-key
 * combos targeted at the nest's cell will shatter it permanently.
 *
 * The nest is only visible when within the player's fog reveal radius (like
 * bats and shadow walkers).
 *
 * States:
 *   - 'idle'       : dormant, waiting for player proximity
 *   - 'active'     : currently spawning bats (brief burst)
 *   - 'cooldown'   : recently activated, waiting before next trigger
 *   - 'destroyed'  : shattered by hammer, permanently inactive
 */

import Phaser from 'phaser';
import { TEX_BAT_NEST, TEX_BAT_NEST_DESTROYED } from '../utils/constants.js';
import {
  BAT_NEST_SIZE,
  BAT_NEST_COOLDOWN,
  BAT_NEST_HAMMER_HITS,
  TILE_SIZE,
  AUDIO_BAT_NEST_DESTROY,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

/** @enum {string} */
const NestState = Object.freeze({
  IDLE: 'idle',
  ACTIVE: 'active',
  COOLDOWN: 'cooldown',
  DESTROYED: 'destroyed',
});

export { NestState };

export default class BatNestEntity extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} col        - Grid column (bridge-wall cell in the expanded grid).
   * @param {number} row        - Grid row (bridge-wall cell in the expanded grid).
   */
  constructor(scene, col, row) {
    const worldX = col * TILE_SIZE + TILE_SIZE / 2;
    const worldY = row * TILE_SIZE + TILE_SIZE / 2;
    super(scene, worldX, worldY, TEX_BAT_NEST);

    this.scene = scene;

    /** @type {number} Grid column of the bridge-wall cell this nest sits on. */
    this.gridCol = col;
    /** @type {number} Grid row of the bridge-wall cell this nest sits on. */
    this.gridRow = row;

    /** @type {string} Current nest state. */
    this._state = NestState.IDLE;

    /** @type {number} Hammer hit combo count accumulated. */
    this._hammerHits = 0;

    /** @type {number} Remaining cooldown time in ms (tracked by system). */
    this._cooldownRemaining = 0;

    /** @type {number} Total activations count for save/restore. */
    this._activationCount = 0;

    // Add to scene and configure rendering
    scene.add.existing(this);
    this.setDisplaySize(BAT_NEST_SIZE, BAT_NEST_SIZE);
    this.setDepth(3); // Above walls (depth 2), same as crumbled overlays
    this.setAlpha(0); // Hidden until player reveals via fog
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /** @returns {string} Current nest state. */
  get nestState() {
    return this._state;
  }

  /** @returns {boolean} True if the nest can be activated (idle). */
  get canActivate() {
    return this._state === NestState.IDLE;
  }

  /** @returns {boolean} True if the nest is permanently destroyed. */
  get isDestroyed() {
    return this._state === NestState.DESTROYED;
  }

  /** @returns {number} Current hammer hits received. */
  get hammerHits() {
    return this._hammerHits;
  }

  /** @returns {number} Total activation count. */
  get activationCount() {
    return this._activationCount;
  }

  /**
   * Transition to active state (spawning bats).
   * The system handles the actual bat spawning.
   */
  activate() {
    if (this._state !== NestState.IDLE) return;
    this._state = NestState.ACTIVE;
    this._activationCount++;

    // Visual pulse on activation — brief bright flash
    if (this.scene) {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1.3,
        scaleY: 1.3,
        duration: 150,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Called by the system after all bats have been spawned.
   * Transitions to cooldown state.
   */
  startCooldown() {
    this._state = NestState.COOLDOWN;
    this._cooldownRemaining = BAT_NEST_COOLDOWN;

    // Dim the nest slightly during cooldown
    if (this.scene && this.alpha > 0) {
      this.scene.tweens.add({
        targets: this,
        alpha: Math.max(0.4, this.alpha * 0.5),
        duration: 300,
        ease: 'Sine.easeOut',
      });
    }
  }

  /**
   * Update cooldown timer. Called by the system each frame.
   * @param {number} delta - ms since last frame.
   */
  updateCooldown(delta) {
    if (this._state !== NestState.COOLDOWN) return;

    this._cooldownRemaining -= delta;
    if (this._cooldownRemaining <= 0) {
      this._cooldownRemaining = 0;
      this._state = NestState.IDLE;

      // Restore full alpha (will be set by fog system to actual visibility)
      if (this.scene) {
        this.scene.tweens.add({
          targets: this,
          alpha: 1,
          duration: 300,
          ease: 'Sine.easeIn',
        });
      }
    }
  }

  /**
   * Register a hammer hit on this nest.
   * @returns {boolean} True if the nest was destroyed by this hit.
   */
  hammerHit() {
    if (this._state === NestState.DESTROYED) return false;

    this._hammerHits++;

    // Vibrate the nest sprite on each hit
    if (this.scene) {
      const baseX = this.x;
      this.scene.tweens.add({
        targets: this,
        x: baseX + 2,
        duration: 40,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          if (this.active) this.x = baseX;
        },
      });
    }

    // Check if enough hits to destroy
    if (this._hammerHits >= BAT_NEST_HAMMER_HITS) {
      this._destroy();
      return true;
    }

    return false;
  }

  /**
   * Set fog visibility — only show nest when within player's fog reveal radius.
   * @param {boolean} visible - Whether the nest should be visible.
   */
  setFogVisible(visible) {
    if (this._state === NestState.DESTROYED) {
      // Destroyed nests show the destroyed texture at reduced alpha
      this.setAlpha(visible ? 0.4 : 0);
      return;
    }

    if (this._state === NestState.COOLDOWN) {
      // Cooldown nests are dimmer
      this.setAlpha(visible ? 0.5 : 0);
      return;
    }

    this.setAlpha(visible ? 1 : 0);
  }

  /**
   * Serialize nest state for save/restore.
   * @returns {object}
   */
  serialize() {
    return {
      col: this.gridCol,
      row: this.gridRow,
      state: this._state,
      hammerHits: this._hammerHits,
      cooldownRemaining: this._cooldownRemaining,
      activationCount: this._activationCount,
    };
  }

  /**
   * Restore nest state from saved data.
   * @param {object} data
   */
  restore(data) {
    this._hammerHits = data.hammerHits || 0;
    this._activationCount = data.activationCount || 0;
    this._cooldownRemaining = data.cooldownRemaining || 0;

    if (data.state === NestState.DESTROYED) {
      this._state = NestState.DESTROYED;
      this.setTexture(TEX_BAT_NEST_DESTROYED);
      this.setAlpha(0); // Fog will control visibility
    } else if (data.state === NestState.COOLDOWN) {
      this._state = NestState.COOLDOWN;
    } else {
      this._state = NestState.IDLE;
    }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Permanently destroy the nest — shatter animation + swap texture.
   * @private
   */
  _destroy() {
    this._state = NestState.DESTROYED;

    // Play destruction sound
    try {
      this.scene.sound.play(AUDIO_BAT_NEST_DESTROY, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio may be missing */ }

    // Shatter particle effect
    if (this.scene) {
      this._playShatterEffect();
    }

    // Swap to destroyed texture with fade
    this.setTexture(TEX_BAT_NEST_DESTROYED);
    if (this.scene) {
      this.scene.tweens.add({
        targets: this,
        alpha: 0.4,
        duration: 500,
        ease: 'Quad.easeOut',
      });
    }
  }

  /**
   * Particle burst effect on nest destruction.
   * @private
   */
  _playShatterEffect() {
    if (!this.scene) return;

    const cx = this.x;
    const cy = this.y;
    const gfx = this.scene.add.graphics();
    gfx.setDepth(7); // Above walls, like burst particles

    const particles = [];
    const count = 8;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
      const speed = 30 + Math.random() * 40;
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1.5 + Math.random() * 1.5,
        alpha: 1,
      });
    }

    const alphaObj = { val: 1 };

    this.scene.tweens.add({
      targets: alphaObj,
      val: 0,
      duration: 600,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        gfx.clear();
        for (const p of particles) {
          p.x += p.vx * 0.016;
          p.y += p.vy * 0.016;
          p.vx *= 0.95;
          p.vy *= 0.95;
          gfx.fillStyle(0x332211, alphaObj.val);
          gfx.fillCircle(p.x, p.y, p.size);
        }
      },
      onComplete: () => {
        gfx.destroy();
      },
    });

    // Brief white flash at nest center
    const flash = this.scene.add.graphics();
    flash.setDepth(7);
    flash.fillStyle(0xffffff, 0.7);
    flash.fillCircle(cx, cy, TILE_SIZE * 0.6);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 300,
      ease: 'Quad.easeOut',
      onComplete: () => {
        flash.destroy();
      },
    });
  }

  /**
   * Clean up on destroy.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    this.scene = null;
    super.destroy(fromScene);
  }
}
